import React, { useEffect, useState, useCallback } from 'react';
import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { FETCH_STATE } from 'constants';
import { Table } from 'components/UI';
import { useStyles } from './Common.styled';
import { partnersActions } from 'state/modules/partners';
import { Box, Tooltip, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useConfirm } from 'hooks';

const InviteListModal = ({ open, handleCancel }) => {
  const data = useSelector((state) => state.partners.invites);
  const pagination = useSelector((state) => state.partners.invitesPagination);
  const fetchingInvites = useSelector((state) => state.partners.fetchingInvites);
  const removeInvite = useSelector((state) => state.partners.removeInvite);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const [currentInvitationId, setCurrentInvitationId] = useState(null);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });

  const [toggleRemovePartnerInvitation, RemovePartnerInvitationConfirm] = useConfirm({
    title: i18n.t('partners.remove_invite_title'),
    message: i18n.t('partners.remove_invite_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.remove'),
    onConfirm: () => {
      if (currentInvitationId) {
        dispatch(partnersActions.removePartnerInvite(currentInvitationId));
        setCurrentInvitationId(null);
      }
    },
    onCancel: () => {
      setCurrentInvitationId(null);
    },
  });

  const loading = fetchingInvites === FETCH_STATE.LOADING;
  const refetch = removeInvite === FETCH_STATE.DONE;

  const fetchInvites = useCallback(
    (params = {}) => {
      dispatch(partnersActions.fetchInvites(params));
    },
    [dispatch]
  );

  useEffect(() => {
    if (open) {
      fetchInvites(params);
    }
  }, [fetchInvites, params, open]);

  useEffect(() => {
    if (open && refetch) {
      fetchInvites(params);
    }
  }, [fetchInvites, params, open, refetch]);

  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    updateParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit };
    updateParams(newParams);
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    updateParams(newParams);
  };

  const updateParams = (newParams) => {
    setParams(newParams);
  };

  const handleRemove = (row) => {
    setCurrentInvitationId(row.id);
    toggleRemovePartnerInvitation();
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '4rem',
      sortable: true,
    },
    {
      name: i18n.t('partners.partner_name'),
      selector: 'name',
      format: (row) => row?.meta_data?.org_name || '--',
      grow: 2,
    },
    {
      name: i18n.t('partners.partner_contact'),
      selector: 'contact_name',
      format: (row) => row?.meta_data?.contact_name || '--',
      grow: 1,
      omit: sharingType === 'FFB',
    },
    {
      name: i18n.t('partners.partner_email'),
      selector: 'status',
      format: (row) => row?.meta_data?.contact_email || '--',
      grow: 3,
    },
    {
      name: i18n.t('partners.partner_phone'),
      selector: 'status',
      format: (row) => row?.meta_data?.contact_phone || '--',
      grow: 2,
    },
    {
      name: i18n.t('common.actions'),
      center: true,
      grow: 0,
      minWidth: '9rem',
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            {(sharingType === 'FFB' && row.status === 'REJECTED') || sharingType === 'FAAS' ? (
              <Tooltip title={i18n.t('common.remove')}>
                <IconButton aria-label="archive" size="medium" onClick={() => handleRemove(row)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            ) : (
              i18n.t('common.n/a')
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <BaseModal
      size="lg"
      open={open}
      hasEnterButton={false}
      hasCancelButton={false}
      onEnter={handleCancel}
      onCancel={handleCancel}
      enableCloseButton={true}
      handleClick={handleCancel}
      enterButtonLabel={i18n.t('partners.invite')}
      title={i18n.t('partners.invite_list_title')}
      hasHeader={true}
      hasFooter={false}
    >
      <RemovePartnerInvitationConfirm />
      <div className={classes.confirmContainer}>
        <Table
          data={data}
          columns={columns}
          onSort={handleSort}
          loading={loading}
          handlePageChange={handlePageChange}
          totalRows={pagination?.totalRows || 0}
          handlePerRowsChange={handlePerRowsChange}
          page={params?.page}
          limit={params?.limit}
          defaultSortField={params?.sort_field}
          defaultSortAsc={!params?.sort_desc}
        />
      </div>
    </BaseModal>
  );
};

export { InviteListModal };
